import React, { useEffect, useState } from 'react';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { Stack, Image, Button } from 'react-bootstrap';
import {
	useNotificationAcceptConnectionRequestMutation,
	useNotificationListQuery,
	useNotificationReadAllMutation,
	useNotificationReadMutation,
	useNotificationRemoveConnectRequestMutation,
} from 'services';
import { dateFormatForNotifications, getNotificationURL } from 'utils';
import useTranslate from 'hooks/useTranslate';
import useAuthStore, { updateUserInfo } from 'store/auth';
import { S3_URL, USER_TYPES } from 'constants/index';
import PaginationBox from 'components/PaginationBox';
import { useNavigate } from 'react-router-dom';
import './styles.scss';
import { setNewNotificationAlert } from 'store/user';
import { setModalConfig } from 'store/common';

const Notification = ({ notification, getRefetch }: any) => {
	const { mutateAsync: acceptRequestMutation } = useNotificationAcceptConnectionRequestMutation();
	const { mutateAsync: rejectRequestMutation } = useNotificationRemoveConnectRequestMutation();

	const userDetails: any = useAuthStore((state) => state.userInfo.user);
	const { translate } = useTranslate();
	const { message, triggeredByUser, notifyType } = notification;
	const userName =
		triggeredByUser?.userType === USER_TYPES.COMPANY
			? triggeredByUser?.company?.companyName
			: triggeredByUser?.seeker?.userName;

	// Function to highlight the userName in the message
	const getHighlightedMessage = (msg: any, name: any) => {
		const parts = msg.split(new RegExp(`(${name})`, 'gi'));
		return parts.map((part: any, index: any) =>
			part?.toLowerCase() === name?.toLowerCase() ? <span key={index}>{part}</span> : part,
		);
	};

	// Function to truncate text with word limit
	const truncateText = (text: string, wordLimit: number = 20) => {
		const words = text.split(' ');
		if (words.length <= wordLimit) {
			return text;
		}
		return words.slice(0, wordLimit).join(' ');
	};

	// Function to render message with conditional truncation
	const renderMessage = () => {
		if (notifyType === 'emailBroadcast') {
			const truncatedMessage = truncateText(message, 10);
			const isTruncated = message.split(' ').length > 10;

			return (
				<p className="mb-0">
					<>
						{getHighlightedMessage(truncatedMessage, userName)}
						{isTruncated && (
							<>
								...{' '}
								<Button
									variant="link"
									className="p-0 text-primary text-decoration-none border-0"
									onClick={() => {
										setModalConfig({
											visible: true,
											type: 'notificationDetail',
											data: { message: message },
										});
									}}
								>
									{translate('Common.seeMore')}
								</Button>
							</>
						)}
					</>
				</p>
			);
		}

		return <p className="mb-0">{getHighlightedMessage(message, userName)}</p>;
	};

	const handleAcceptRequest = (id: any, notificationId: any) => {
		updateUserInfo({ connection: userDetails?.connection + 1 });
		acceptRequestMutation({ id, notificationId })
			.then((_res: any) => {
				if (_res?.success) {
					getRefetch();
				} else {
					updateUserInfo({ connection: userDetails?.connection - 1 });
				}
			})
			.catch((err: any) => {
				console.log(err);
			});
	};

	const handleRejectRequest = (id: any, notificationId: any) => {
		rejectRequestMutation({ id, notificationId })
			.then((_res: any) => {
				getRefetch();
			})
			.catch((err: any) => {
				console.log(err);
			});
	};
	return (
		<div>
			{renderMessage()}

			{/* Conditionally render buttons if notifyType is connectionRequest */}
			{notification?.notifyType === 'connectionRequest' && (
				<div className="d-flex gap-2 mt-auto">
					<Button
						onClick={() =>
							handleAcceptRequest(notification?.triggeredByUser?.id, Number(notification?.id))
						}
						className="filled-btn rounded-pill lh-1"
					>
						{translate('Button.accept')}
					</Button>
					<Button
						onClick={() =>
							handleRejectRequest(notification?.triggeredByUser?.id, Number(notification?.id))
						}
						className="gray-transparent-btn rounded-pill lh-1"
					>
						{translate('Button.reject')}
					</Button>
				</div>
			)}
		</div>
	);
};

const Notifications: React.FC = () => {
	const { translate } = useTranslate();
	const [currentPage, setCurrentPage] = useState(1);
	const payload = `limit=15&page=${currentPage}`;
	const { data: notifications = {}, refetch } = useNotificationListQuery(payload);
	const userDetails: any = useAuthStore((state) => state.userInfo.user);
	const { mutateAsync: notificationReadAMutation } = useNotificationReadMutation();
	const navigate = useNavigate();

	useEffect(() => {
			setNewNotificationAlert(notifications?.meta?.unreadCount);
		}, [notifications?.meta?.unreadCount]);

	useEffect(() => {
		refetch();
	}, [currentPage]);

	const handleClickOnNotification = async (
		type: string,
		id: any,
		redirectOn: string | null = null,
		data: any,
		notificationId: any,
		isRead: boolean
	) => {

		if (!isRead) {
			await notificationReadAMutation({ id: notificationId });
			refetch();
		}
		if (type === 'profileView') {
			return navigate(`/${data?.userType}/profile/${data?.id}`);
		}
		const url: any = await getNotificationURL(type, id, null, userDetails?.userType, redirectOn);
		navigate(url);
	};

	const goToProfile = (data: any) => {
		navigate(`/${data?.userType}/profile/${data?.id}`);
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold mb-0">{translate('Common.notification')}</h4>
					</div>
				</div>
				<Stack>
					{notifications?.docs?.length > 0 ? (
						notifications?.docs?.map((notification: any) => {
							const { userType } = notification?.triggeredByUser || {};
							return (
								<div
									key={`${notification?.userId}-${notification?.id}`}
									className="notification-box d-flex align-items-md-center position-relative"
									style={notification?.isRead ? {} : { backgroundColor: '#e9e9e9' } }
								>
									<div className="notification-box-profile">
										<Image
											onClick={() => {
												if (notification?.notifyType === 'emailBroadcast') return;
												goToProfile(notification?.triggeredByUser);
											}}
											style={{ cursor: 'pointer' }}
											src={
												notification?.triggeredByUser?.[userType]?.profilePicture
													? `${S3_URL}${notification?.triggeredByUser?.[userType]?.profilePicture}`
													: IMAGE_PATHS.UserDefault
											}
											onError={({ currentTarget }) => {
												currentTarget.onerror = null;
												currentTarget.src = IMAGE_PATHS.UserDefault;
											}}
											alt="profile"
											className="w-100 h-100 rounded-circle object-fit-cover"
											loading="lazy"
										/>
									</div>
									<div className="notification-box-content d-flex flex-column flex-lg-row gap-2 gap-lg-3 flex-1">
										<div className="notification-msg flex-1">
											<p
												className="mb-0"
												onClick={() =>
													handleClickOnNotification(
														notification?.notifyType,
														notification?.notificationTypeId,
														userType,
														notification?.triggeredByUser,
														notification?.id,
														notification?.isRead
													)
												}
											>
												<Notification notification={notification} getRefetch={refetch} />
											</p>
										</div>
										<div className="notification-time">
											<p className="mb-0">{dateFormatForNotifications(notification?.createdAt)}</p>
										</div>
									</div>
								</div>
							);
						})
					) : (
						<Stack className="posted-job-box">
							<p className="mb-0">{translate('Common.noNotificationFound')}</p>{' '}
						</Stack>
					)}
				</Stack>
				{notifications?.meta?.totalDocs > notifications?.meta?.limit && (
					<PaginationBox
						totalData={notifications?.meta?.totalDocs}
						limit={notifications?.meta?.limit}
						page={currentPage}
						setCurrentPage={setCurrentPage}
					/>
				)}
			</Stack>
		</div>
	);
};

export default Notifications;
